import { IOErrorLOFactory } from "./../../../externalLogging/factories/IOErrorLOFactory";
import { ExternalLog } from "./../../../externalLogging/ExternalLog";
import { CommandController } from "./../../../commanding/CommandController";
import { SimpleCommand } from "./../../../commanding/SimpleCommand";
import { EnvGlobalsHandler } from "../../EnvGlobalsHandler";
import { IEnvironmentGlobals } from "../../IEnvironmentGlobals";
import { BasicModel } from "../../model/BasicModel";
import { ForcedMaintenanceEnum } from "../../startup/ForcedMaintenanceEnum";
import { URLRequest, URLLoaderService } from "@goodgamestudios/createjs-utilities";
import { BasicConstants } from "../../constants/BasicConstants";
import { ClientFunnelGameStates } from "../../../tracking/clientFunnel/ClientFunnelGameStates";
import { ClientFunnelTrackingController } from "../../../tracking/clientFunnel/ClientFunnelTrackingController";
import { PathProvider } from "../../environment/providers/PathProvider";
import { getLogger } from "@goodgamestudios/logger";

import Event = createjs.Event;
import IOErrorEvent = createjs.IOErrorEvent;
import SecurityErrorEvent = createjs.SecurityErrorEvent;

const logger = getLogger("Connection.BasicCheckMaintenanceCommand");

export class BasicCheckMaintenanceCommand extends SimpleCommand {
    private MAINTENANCE_TIMER_DELAY: number = 60;
    private noMaintenanceCommandName: string;


    /**
     * used for display how long (in seconds)  maintenance will last from now
     */
    protected maintenanceTimeInSeconds: number;

    private GGS_UTC_LOAD_TRIGGER_OFFSET_IN_SECONDS: number = 900;
    private UTC_REQUEST_URL: string = BasicConstants.DOMAIN_PROTOCOL + "://time.goodgamestudios.com";


    private reloadTimeout: number;

    public execute(commandVars: any = null): void {
        if (commandVars == null) {
            this.noMaintenanceCommandName = null;
        }
        else {
            this.noMaintenanceCommandName = (<string>commandVars);
        }
        this.executeDefaultBehavior();
    }

    /**
     * override this function if your project does not have any maintenance functionality
     * use executeNoMaintenance in your overwritten method
     *
     **/
    protected executeDefaultBehavior(): void {
        if (this.env.urlVariables.forcedMaintenance == ForcedMaintenanceEnum.OFF) {
            this.executeNoMaintenance();
            return;
        }
        if (this.env.urlVariables.forcedMaintenance == ForcedMaintenanceEnum.ON) {
            this.setMaintenance(true);
            return;
        }
        else {
            this.loadMaintenance(PathProvider.instance.maintenanceConfigURL);
        }
    }


    private loadMaintenance(url: string): void {
        const loader = new URLLoaderService(new URLRequest(url));
        loader.load().then(response => {
            loader.dispose();
            try {
                this.checkMaintenance(JSON.parse(response));
            }
            catch (error) {
                logger.warn("Error while executing something in checkMaintenance: ", error);
                (<any>window).bugsnagClient.notify(error);
            }

        })
            .catch(error => {
                logger.warn("IOError (" + error + ") while loading Maintenancecheck");
                ExternalLog.logger.log(
                    new IOErrorLOFactory(
                        IOErrorLOFactory.GENERAL_LOADER_IO_ERROR,
                        error.toString(),
                        url
                    )
                );
                this.executeNoMaintenance();
            })

    }

    /**
     * iterates through maintData
     * set maintenance if zoneId machtes and maintenanceTime higher than unixTime
     **/
    private checkMaintenance(json: {[zoneId: number]: string}): void {

        const currentZone: string = BasicModel.instanceProxy.selectedInstanceVO.zoneId.toString();
        let zoneInMaintenance = Object.keys(json).find(zoneId => zoneId == currentZone);
        if (zoneInMaintenance) {
            // UTC (seconds) until maintenance will be active
            const endOfMaintenanceUTC :number = parseInt(json[currentZone]);
            const endOfMaintananceDate :Date= new Date(endOfMaintenanceUTC * 1000);
            logger.debug(`${zoneInMaintenance} will be in maintenance until ${endOfMaintananceDate}`);

            const clientUTC = Date.now() / 1000;
            let serverUTC;


            const checkIfMaintenance = (duration) => {
                if (duration <= 0) {
                    logger.debug("Mainteance is over!")
                    this.setMaintenance(false);
                }
                else {
                    clearTimeout(this.reloadTimeout);
                    let delay: number = Math.min(this.MAINTENANCE_TIMER_DELAY, duration);
                    logger.debug(`MaintenanceTimer Default delay ${this.MAINTENANCE_TIMER_DELAY} vs  remainingMaintenance ${duration } --> will reload file in ${delay} seconds`);
                    this.reloadTimeout = window.setTimeout(() => this.loadMaintenance(PathProvider.instance.maintenanceConfigURL), delay * 1000);
                    this.setMaintenance(true);
                }
            }

            if (endOfMaintenanceUTC > clientUTC) {

                // used for display how long (in seconds)  maintenance will last from now
                this.maintenanceTimeInSeconds = (endOfMaintenanceUTC - clientUTC);
                logger.debug(`Maintenance will last for ${this.maintenanceTimeInSeconds} more seconds `);


                //time difference could be to long because of wrong client utc  (normally maintenaince does not last long - if, according to the client utc, duration appears to be longer than our treshold , load the GGS Time Service to make sure we are calculating maintenance time with right numbers)
                if (this.maintenanceTimeInSeconds > this.GGS_UTC_LOAD_TRIGGER_OFFSET_IN_SECONDS) {
                    const loader = new URLLoaderService(new URLRequest(this.UTC_REQUEST_URL))
                    loader.load().then(response => {
                            serverUTC = Number(response);
                            logger.debug(`clientUTC ${clientUTC}  -  server UTC ${serverUTC}`);
                            this.maintenanceTimeInSeconds = (endOfMaintenanceUTC - serverUTC);
                            checkIfMaintenance(this.maintenanceTimeInSeconds);
                        }
                    ).catch(error => {
                        logger.warn("IOError (" + error.message + ") while loading UTC - will prodeed without Maintenance behaviour");
                        ExternalLog.logger.log(new IOErrorLOFactory(IOErrorLOFactory.GENERAL_LOADER_IO_ERROR, error.message, this.UTC_REQUEST_URL));
                        this.clearMaintenance(); // if clientUTC is suspicious and GGS-server UTC does not work, simply skip the maintenance behaviour  (if server works it will connect - if it does not it simply wont)
                    });


                }
                else {
                    checkIfMaintenance(this.maintenanceTimeInSeconds);
                }

            }
            else {
                // this should actually never happen because maintenance file is being removed as soon maintenance is over.  might be that clientUTC is wrong ( set in the future) ?  - not really a problem since we are simply not showing the dialog and will try to connect.  if server works, it will succeed otherwise it will fail
                logger.warn("according to maintenance file our zone is under maintenance - but according to client UTC maintenance time is already over");
                this.setMaintenance(false);
            }
        }
        else {
            this.setMaintenance(false);
        }


    }


    /**
     * check for loginIsKeyBased and executes specific maintenancemethod
     *
     * @param isMaintenance:Boolean
     **/
    private setMaintenance(isMaintenance: boolean): void {

        if (isMaintenance) {
            if (this.env.maintenanceMode) {
                this.updateMaintenance();
            }
            else {

                //social
                if (EnvGlobalsHandler.globals.loginIsKeyBased) {
                    this.executeSocialMaintenance();
                }
                //normal world
                else {
                    this.executeNWMaintenance();
                }
            }
            ClientFunnelTrackingController.getInstance().trackState(ClientFunnelGameStates.MAINTENANCE_SCREEN);
        }
        else {
            if (this.env.maintenanceMode) {
                this.clearMaintenance();
            }
            else {
                this.executeNoMaintenance();
            }

        }

        this.env.maintenanceMode = isMaintenance;

    }

    /**
     * Override this function
     **/
    protected updateMaintenance(): void {
        //@override
    }

    /**
     * Override this function
     **/
    protected clearMaintenance(): void {
        clearTimeout(this.reloadTimeout);
        this.executeNoMaintenance();
    }

    /**
     * Defines the behaviour of Game while server is under Maintenance  (NW means "normal-world" as opposed to Social/Network platform)
     **/
    protected executeNWMaintenance(): void {
        this.executeNoMaintenance();
        throw new Error("executeNWMaintenance must be overridden by Game (will proceed with default No_Maintenance behaviour");
    }

    /**
     * Defines the behaviour of Game on Social/Network Platform while server is under Maintenance
     **/
    protected executeSocialMaintenance(): void {
        this.executeNoMaintenance();
        throw new Error("executeSocialMaintenance must be overridden by Game (will proceed with default No_Maintenance behaviour");
    }

    /**
     * typically do Connect
     **/
    protected executeNoMaintenance(): void {
        if (this.noMaintenanceCommandName != null) {
            CommandController.instance.executeCommand(this.noMaintenanceCommandName);
        }
        else {
            logger.debug("No noMaintenanceCommandName set! Nothing will happen!");
        }
    }


    protected /*final*/ get env(): IEnvironmentGlobals {
        return EnvGlobalsHandler.globals;
    }


}

